import { useBottomDrawer } from '@landtrustinc/design-system';
import { useCallback, useState } from 'react';

import type { LtPlusStep } from '../components/LtPlusDrawer/hooks/useLtPlusFlow';
import type { ChatMessage, LtPlusCta, OtpContext } from '../types';

/**
 * Drawer type discriminator
 */
export type DrawerType = 'signup' | 'ltplus' | null;

export type LtPlusReturnFlowState = {
  isReturningToLtPlus: boolean;
  returnStep: Extract<LtPlusStep, 'benefits' | 'checkout'>;
};

/**
 * Drawer state that includes type and associated context
 */
export type DrawerState = {
  type: DrawerType;
  signupContext?: OtpContext;
  signupVariant?: string;
  /** The message that triggered the signup drawer (for updating confirmation state) */
  signupMessage?: ChatMessage;
  ltPlusCta?: LtPlusCta;
  ltPlusMessageId?: string;
  ltPlusInitialStep?: Extract<LtPlusStep, 'benefits' | 'checkout'>;
  ltPlusReturnFlowState?: LtPlusReturnFlowState;
};

/**
 * Custom hook to manage a single bottom drawer with multiple content types
 * Replaces the need for separate drawer instances for signup and LT+ flows
 */
export const useBuckChatDrawer = () => {
  const drawer = useBottomDrawer();
  const [drawerState, setDrawerState] = useState<DrawerState>({ type: null });

  // Open signup drawer with optional context and message
  const openSignup = useCallback(
    (signupContext?: OtpContext, message?: ChatMessage, signupVariant?: string) => {
      setDrawerState({
        type: 'signup',
        signupContext,
        signupMessage: message,
        signupVariant: signupVariant ?? message?.variant,
      });
      drawer.open(null);
    },
    [drawer]
  );

  // Open LT+ benefits drawer
  const openLtPlusBenefits = useCallback(
    (ltPlusCta: LtPlusCta, ltPlusMessageId?: string) => {
      setDrawerState({
        type: 'ltplus',
        ltPlusCta,
        ltPlusMessageId,
        ltPlusInitialStep: 'benefits',
        ltPlusReturnFlowState: {
          isReturningToLtPlus: false,
          returnStep: 'benefits',
        },
      });
      drawer.open(null);
    },
    [drawer]
  );

  // Retained for transitional compatibility; LT+ now uses one drawer type.
  const navigateToCheckout = useCallback(() => {
    setDrawerState((prev) => ({
      ...prev,
      type: 'ltplus',
      ltPlusInitialStep: 'checkout',
    }));
  }, []);

  // Retained for transitional compatibility; LT+ now uses one drawer type.
  const navigateBackToBenefits = useCallback(() => {
    setDrawerState((prev) => ({
      ...prev,
      type: 'ltplus',
      ltPlusInitialStep: 'benefits',
    }));
  }, []);

  const navigateToSignupFromLtPlus = useCallback((signupContext?: OtpContext) => {
    setDrawerState((prev) => ({
      type: 'signup',
      signupContext,
      signupVariant: 'ltplus',
      ltPlusCta: prev.ltPlusCta,
      ltPlusMessageId: prev.ltPlusMessageId,
      ltPlusInitialStep: 'checkout',
      ltPlusReturnFlowState: {
        isReturningToLtPlus: true,
        returnStep: 'checkout',
      },
    }));
  }, []);

  // Close drawer and reset state
  const close = useCallback(() => {
    drawer.close();
    // Reset state after animation completes
    setTimeout(() => {
      setDrawerState({ type: null });
    }, 300);
  }, [drawer]);

  return {
    drawerProps: drawer.drawerProps,
    drawerState,
    openSignup,
    openLtPlusBenefits,
    navigateToCheckout,
    navigateBackToBenefits,
    navigateToSignupFromLtPlus,
    close,
  };
};
